import { atom } from 'jotai';
import { atomWithDefault, RESET, unwrap, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { ICompanyMetricsDataModel } from '../../../data-models/metrics.data-model';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { selectedCompanyIdStateFinanceJ } from '../../../services/state/PortCosStateJ';
import { getForesightStore } from '../../../util/jotai-store';

/**
 * Current step (zero-indexed) for the multi-step transaction / investment forms.
 *
 * Default returns `1` if a company is already selected at first read (so the
 * form starts past the company-select step), else `0`. Reads
 * `selectedCompanyIdStateFinanceJ` via a non-reactive store peek — once the
 * user starts navigating steps the value is set explicitly and the derived
 * default never re-fires.
 */
export const currentStepStateFinanceJ = atomWithDefault<number>(() => {
  return getForesightStore().get(selectedCompanyIdStateFinanceJ) ? 1 : 0;
});

export const selectedCompanyIdState = atomWithDefault<number | null>((get) => {
  return get(selectedCompanyIdStateFinanceJ);
});

export const companyDataState = atom<ICompanyDataModel | null>((get) => {
  const selectedCompanyId = get(selectedCompanyIdState);
  if (!selectedCompanyId) {
    return null;
  }
  return get(unwrap(companyStateJ(selectedCompanyId))) ?? null;
});

export const investmentDataState = atom<Partial<IFinanceRoundDataModel> | null>(null);

export const transactionDataState = atom<Partial<ITransactionDataModel> | null>(null);

export const transactionPreviewState = atom<ICompanyMetricsDataModel | null>(null);

export function useResetTransactionUIState() {
  return useAtomCallback(
    useCallback((_get, set) => {
      set(selectedCompanyIdState, RESET);
      set(currentStepStateFinanceJ, RESET);
      set(investmentDataState, null);
      set(transactionDataState, null);
      set(transactionPreviewState, null);
    }, [])
  );
}
